import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatPhoneNumber } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Plus, Pencil, Trash2, Star, MapPin, Phone, Mail, Users } from 'lucide-react';

interface BusinessProfile {
  id: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  taxId: string | null;
  logoUrl: string | null;
  isDefault: boolean;
  isActive: boolean;
  fiscalYearEndMonth: number;
}

const FYE_MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December (calendar year)' },
];

const emptyForm = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: 'USA',
  phone: '',
  email: '',
  website: '',
  taxId: '',
  logoUrl: '',
  isDefault: false,
  fiscalYearEndMonth: 12,
};

export function BusinessProfilesPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<BusinessProfile | null>(null);
  const [form, setForm] = useState(emptyForm);

  const { data, isLoading } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: async () => {
      const result = await api.getBusinessProfiles();
      return (result.data || []) as BusinessProfile[];
    },
  });

  const profiles = data || [];

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createBusinessProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-profiles'] });
      closeDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateBusinessProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-profiles'] });
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteBusinessProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-profiles'] });
    },
  });

  function openCreate() {
    setEditingProfile(null);
    setForm(emptyForm);
    setDialogOpen(true);
  }

  function openEdit(profile: BusinessProfile) {
    setEditingProfile(profile);
    setForm({
      name: profile.name,
      addressLine1: profile.addressLine1 || '',
      addressLine2: profile.addressLine2 || '',
      city: profile.city || '',
      state: profile.state || '',
      zip: profile.zip || '',
      country: profile.country || 'USA',
      phone: profile.phone || '',
      email: profile.email || '',
      website: profile.website || '',
      taxId: profile.taxId || '',
      logoUrl: profile.logoUrl || '',
      isDefault: profile.isDefault,
      fiscalYearEndMonth: profile.fiscalYearEndMonth ?? 12,
    });
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingProfile(null);
    setForm(emptyForm);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      ...form,
      addressLine1: form.addressLine1 || null,
      addressLine2: form.addressLine2 || null,
      city: form.city || null,
      state: form.state || null,
      zip: form.zip || null,
      phone: form.phone || null,
      email: form.email || null,
      website: form.website || null,
      taxId: form.taxId || null,
      logoUrl: form.logoUrl || null,
    };

    if (editingProfile) {
      updateMutation.mutate({ id: editingProfile.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Business Profiles</h1>
          <p className="text-muted-foreground">
            Manage the business entities you invoice under
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Business Profile
        </Button>
      </div>

      {profiles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-1">No business profiles yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first business profile to start invoicing
            </p>
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Business Profile
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((profile) => (
            <Card key={profile.id} className={!profile.isActive ? 'opacity-60' : ''}>
              <CardContent className="pt-6">
                {profile.logoUrl && (
                  <img
                    src={profile.logoUrl}
                    alt={`${profile.name} logo`}
                    className="h-10 max-w-[150px] object-contain mb-2"
                  />
                )}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-lg">{profile.name}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    {profile.isDefault && (
                      <Badge variant="info">
                        <Star className="h-3 w-3 mr-1" />
                        Default
                      </Badge>
                    )}
                    {!profile.isActive && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                </div>

                {(profile.addressLine1 || profile.city) && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground mb-2">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      {profile.addressLine1 && <div>{profile.addressLine1}</div>}
                      {profile.addressLine2 && <div>{profile.addressLine2}</div>}
                      {(profile.city || profile.state || profile.zip) && (
                        <div>
                          {[profile.city, profile.state].filter(Boolean).join(', ')}
                          {profile.zip && ` ${profile.zip}`}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {profile.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Phone className="h-4 w-4" />
                    {formatPhoneNumber(profile.phone)}
                  </div>
                )}

                {profile.email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Mail className="h-4 w-4" />
                    {profile.email}
                  </div>
                )}

                {profile.taxId && (
                  <div className="text-xs text-muted-foreground mb-3">
                    Tax ID: {profile.taxId}
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => openEdit(profile)}>
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this business profile?')) {
                        deleteMutation.mutate(profile.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProfile ? 'Edit Business Profile' : 'New Business Profile'}
            </DialogTitle>
            <DialogDescription>
              {editingProfile
                ? 'Update your business profile information'
                : 'Add a new business entity for invoicing'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Business Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: formatPhoneNumber(e.target.value) })}
                  placeholder="xxx-xxx-xxxx"
                  maxLength={12}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="addressLine1">Address Line 1</Label>
              <Input
                id="addressLine1"
                value={form.addressLine1}
                onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="addressLine2">Address Line 2</Label>
              <Input
                id="addressLine2"
                value={form.addressLine2}
                onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="zip">ZIP</Label>
                <Input
                  id="zip"
                  value={form.zip}
                  onChange={(e) => setForm({ ...form, zip: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="taxId">Tax ID (EIN)</Label>
                <Input
                  id="taxId"
                  value={form.taxId}
                  onChange={(e) => setForm({ ...form, taxId: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="fiscalYearEndMonth">Fiscal Year End Month</Label>
              <select
                id="fiscalYearEndMonth"
                value={form.fiscalYearEndMonth}
                onChange={(e) => setForm({ ...form, fiscalYearEndMonth: parseInt(e.target.value, 10) })}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {FYE_MONTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Used for FYTD calculations across the dashboards. FY is named for its starting year (e.g.,
                FY 2025 with July end = Aug 1, 2025 – Jul 31, 2026).
              </p>
            </div>

            <div>
              <Label>Logo</Label>
              <div className="space-y-2">
                {form.logoUrl && (
                  <div className="flex items-center gap-3">
                    <img
                      src={form.logoUrl}
                      alt="Logo preview"
                      className="h-16 w-16 object-contain border rounded"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm({ ...form, logoUrl: '' })}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const result = await api.uploadLogo(file);
                    if (result.success && result.data) {
                      setForm({ ...form, logoUrl: result.data.logoUrl });
                    } else {
                      alert('Failed to upload logo. Max size is 2MB.');
                    }
                    e.target.value = '';
                  }}
                />
                <Input
                  placeholder="Or enter a URL: https://..."
                  value={form.logoUrl}
                  onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={form.isDefault}
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isDefault">Set as default billing profile</Label>
            </div>

            {/* Owners section — only meaningful for an existing profile */}
            {editingProfile && (
              <OwnersSection profileId={editingProfile.id} />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingProfile ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================
// Owners (shareholders / partners / members)
// Scaffolding for Form 1120-S / Form 1065 K-1 generation when those forms
// are added. Currently informational only — no tax form consumes this data.
// ============================================
const OWNER_ROLES = [
  { value: 'shareholder', label: 'Shareholder (S/C-Corp)' },
  { value: 'partner', label: 'Partner (Partnership)' },
  { value: 'member', label: 'Member (LLC)' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'other', label: 'Other' },
];

interface BusinessOwner {
  id: string;
  name: string;
  tinLast4: string | null;
  ownershipPct: number;
  role: string;
  isActive: boolean;
  effectiveDate: string;
  endDate: string | null;
  isOfficer: boolean;
  notes: string | null;
}

function OwnersSection({ profileId }: { profileId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['business-owners', profileId],
    queryFn: () => api.getBusinessOwners(profileId),
  });
  const owners = (data?.data || []) as BusinessOwner[];
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    tinLast4: '',
    ownershipPct: '',
    role: 'shareholder',
    isOfficer: false,
  });

  const createMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.createBusinessOwner(profileId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['business-owners', profileId] });
      setShowForm(false);
      setDraft({ name: '', tinLast4: '', ownershipPct: '', role: 'shareholder', isOfficer: false });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (ownerId: string) => api.deleteBusinessOwner(profileId, ownerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business-owners', profileId] }),
  });

  const totalActivePct = owners
    .filter(o => o.isActive)
    .reduce((s, o) => s + o.ownershipPct, 0);

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const pct = parseFloat(draft.ownershipPct);
    if (!draft.name || !Number.isFinite(pct)) return;
    createMut.mutate({
      name: draft.name,
      tinLast4: draft.tinLast4 || null,
      ownershipPct: pct,
      role: draft.role,
      isOfficer: draft.isOfficer,
    });
  }

  return (
    <div className="rounded-md border p-3 bg-muted/30 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Users className="h-4 w-4" />
          Owners <span className="text-xs text-muted-foreground font-normal">({owners.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {owners.length > 0 && (
            <span
              className={`text-xs ${Math.abs(totalActivePct - 100) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}`}
              title="Total active ownership"
            >
              Total: {totalActivePct.toFixed(2)}%
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowForm(s => !s)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Owner
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Required only for K-1 generation (S-Corp / Partnership). Useful record-keeping for any entity.
        Total active ownership should sum to 100%.
      </p>

      {showForm && (
        <form onSubmit={handleAdd} className="grid grid-cols-12 gap-2 p-2 rounded bg-background border">
          <div className="col-span-4">
            <Input
              placeholder="Owner name"
              value={draft.name}
              onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
              required
            />
          </div>
          <div className="col-span-2">
            <Input
              placeholder="TIN last 4"
              maxLength={4}
              value={draft.tinLast4}
              onChange={(e) => setDraft(d => ({ ...d, tinLast4: e.target.value.replace(/\D/g, '') }))}
            />
          </div>
          <div className="col-span-2">
            <Input
              placeholder="% own"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={draft.ownershipPct}
              onChange={(e) => setDraft(d => ({ ...d, ownershipPct: e.target.value }))}
              required
            />
          </div>
          <div className="col-span-3">
            <select
              value={draft.role}
              onChange={(e) => setDraft(d => ({ ...d, role: e.target.value }))}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-xs"
            >
              {OWNER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="col-span-1 flex justify-end">
            <Button type="submit" size="sm" disabled={createMut.isPending}>Add</Button>
          </div>
          <label className="col-span-12 flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={draft.isOfficer}
              onChange={(e) => setDraft(d => ({ ...d, isOfficer: e.target.checked }))}
            />
            Officer (Form 1120 Line 12 — Compensation of Officers)
          </label>
        </form>
      )}

      {isLoading ? (
        <p className="text-xs text-muted-foreground py-2 text-center">Loading owners…</p>
      ) : owners.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2 text-center">No owners added yet.</p>
      ) : (
        <div className="divide-y">
          {owners.map(o => (
            <div key={o.id} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="font-medium flex-1 truncate">{o.name}</span>
              {o.tinLast4 && (
                <span className="text-xs text-muted-foreground font-mono">****{o.tinLast4}</span>
              )}
              <span className="text-xs">{o.ownershipPct.toFixed(2)}%</span>
              <Badge variant="secondary" className="text-xs">
                {OWNER_ROLES.find(r => r.value === o.role)?.label.split(' (')[0] || o.role}
              </Badge>
              {o.isOfficer && <Badge variant="info" className="text-xs">Officer</Badge>}
              {!o.isActive && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive h-7 w-7 p-0"
                onClick={() => {
                  if (confirm(`Remove ${o.name}?`)) deleteMut.mutate(o.id);
                }}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
